import { debounce } from "../../@helpers/js/queue-processing.js";

import ClassNames from "./classNames.js";
import DomElements from "./domElements.js";
import Shared from "./shared.js";
const {
  closeAllSubmenus,
  isAnySubmenuOpen,
} = Shared;
import Overlay from "./handleOverlay.js";
const { reevaluateOverlay } = Overlay;
import { interpretAction } from "../../@helpers/js/interpretAction.js";

const $autoCompleteDesktop = DomElements.$searchAutoCompleteDesktop;
const $autoCompleteMobile = DomElements.$searchAutoCompleteMobile;
const $btnClearDesktop = DomElements.$searchBtnClearDesktop;
const $btnClearMobile = DomElements.$searchBtnClearMobile;
const $buttonToggleSearchMobile = DomElements.$searchButtonToggleSearchMobile;
const $formDesktop = DomElements.$searchFormDesktop;
const $formMobile = DomElements.$searchFormMobile;
const $formWrapperMobile = DomElements.$searchFormWrapperMobile;
const $inputDesktop = DomElements.$searchInputDesktop;
const $inputMobile = DomElements.$searchInputMobile;
const $submitDesktop = DomElements.$searchSubmitDesktop;
const $submitMobile =DomElements.$searchSubmitMobile;
const $nav = DomElements.$navAccount;

/**
 * IMPORTANT
 * We were unable to support a single search form for both desktop and mobile
 * The design is to different
 * So all required functionality needs to be applied to both mobile and desktop
 */

// Array for auto complete results
let autoCompleteResponse = [];
let selectedSearchResultIndex = -1;
let searchInitialized = false;
const site = Array.from(document.documentElement.classList).reduce((prev, current) => {
  if (['cz', 'just', 'zorgkantoor', 'pzp', 'czdirect', 'pzpdirect'].includes(current)) {
    return current;
  }
  return prev;
}, null);

/**
 * Handle initial setup
 */
function initSearch() {
  // prevent double execution
  if (searchInitialized) {
    return;
  }
  searchInitialized = true;

  if ($formMobile || $formDesktop) {
    handleInputFocus();
    appendUserInputToFormAction();
    setSubmitDisabledIfInputIsToSmallDesktop();
    handleSubmitForms();
    handleInputValidation();
    setupAutoComplete();
    handleClearButton();
  }
}

/**
 * If search on mobile is open
 */
export const searchMobileisOpen = () => !!$formWrapperMobile?.classList?.contains(
  `${ClassNames.search.formWrapperMobile}--${ClassNames.modifiers.active}`
);

/**
 * Toggle display search mobile
 */
export const clickToggleSearchMobile = () => {
  $buttonToggleSearchMobile.dispatchEvent(new Event("click"));
}

/**
 * Toggle display search mobile
 */
export const toggleSearchMobile = (action) => {
  const interpretedAction = interpretAction(action);

  // Handle classes
  $formWrapperMobile.classList[interpretedAction](
    `${ClassNames.search.formWrapperMobile}--${ClassNames.modifiers.active}`
  );

  $buttonToggleSearchMobile.classList[interpretedAction](
    `${ClassNames.search.buttonToggleSearchMobile}--${ClassNames.modifiers.active}`
  );

  DomElements.$logo.classList[interpretedAction](
    `${ClassNames.layout.logo}--${ClassNames.modifiers.hidden}`
  );

  if ($nav) {
    $navAccountBtnMobile.classList[interpretedAction](
      `${ClassNames.navAccount.btnMobile}--${ClassNames.modifiers.hidden}`
    );
  }

  // If open focus directly on input
  if (searchMobileisOpen()) $inputMobile.focus();
};

/**
 * Keep both desktop and mobile input in sync
 */
const appendUserInputToFormAction = () => {
  $inputMobile?.addEventListener("keyup", () => {
    setSearchFormAction($inputMobile);
    if ($inputDesktop) {
      $inputDesktop.value = $inputMobile.value;
    }
  });

  $inputDesktop?.addEventListener("keyup", () => {
    setSearchFormAction($inputDesktop);
    if ($inputMobile) {
      $inputMobile.value = $inputDesktop.value;
    }
  });
};

/**
 * Set the action based on user value
 */
const setSearchFormAction = ($input) => {
  const action = `${
    ($formMobile?.action || $formDesktop?.action)?.split("?")[0]
  }?query=${$input.value
    .trim()
    .replace(/ {2,}/g, " ") // Remove multiple spaces
    .replace(/\s/g, "+") // Replace spaces for '+' character
    .replace(/[^+a-zA-Z0-9]/g, "")}`; // Remove all special characters except alfabetical, numeric and '+' characters

  if ($formDesktop) $formDesktop.action = action;
  if ($formMobile) $formMobile.action = action;
};

/**
 * Evaluate if submit button should be dis- or enabled
 */
const evaluateSubmitDisabled = () => {
  if ($inputMobile?.value?.length > 0 && $submitMobile) $submitMobile.disabled = false;
  else if ($submitMobile) $submitMobile.disabled = true;

  if ($inputDesktop?.value?.length > 0 && $submitDesktop) $submitDesktop.disabled = false;
  else if ($submitDesktop) $submitDesktop.disabled = true;
};

/**
 * Clear auto complete results and reset selected result index
 */
const clearAutoCompleteResults = () => {
  // Clear auto complete containers
  if ($autoCompleteMobile) $autoCompleteMobile.innerHTML = "";
  if ($autoCompleteDesktop) $autoCompleteDesktop.innerHTML = "";

  // Reset accessiblity field
  $inputDesktop?.setAttribute("aria-expanded", "false");
  $inputMobile?.setAttribute("aria-expanded", "false");

  // reset selected search index
  selectedSearchResultIndex = -1;

  // reset autocomplete results
  autoCompleteResponse = [];
};

/**
 * Handle API response in UI
 */
const handleAutoComplete = async () => {
  const value = $inputMobile?.value || $inputDesktop?.value;
  if (!value) return;
  const response = await requestAutoComplete(value);

  // Clear all previous results
  clearAutoCompleteResults();

  // Store new response
  autoCompleteResponse = response;

  if (autoCompleteResponse?.length) {


    // List label
    const label = document.createElement("label");
    label.id = ClassNames.search.listLabel;
    label.innerHTML = ($formDesktop?.dataset?.suggestionsLabel || $formMobile?.dataset?.suggestionsLabel) ?? "Suggesties";

    const ol = document.createElement("ol");
    ol.id = ClassNames.search.list;
    ol.setAttribute("role", "listbox");
    autoCompleteResponse.forEach((value, index) => {
      // Create a link, set `href` and set the name
      const a = document.createElement("a");
      a.setAttribute(
        "href",
        `${($formMobile || $formDesktop)?.getAttribute("action")?.split("?")[0]
        }?query=${value.replace(" ", "+")}`
      );
      a.innerHTML = value;
      // Create a `li` node
      const li = document.createElement("li");
      li.id = `option-${index}`;
      li.setAttribute("role", "option");
      // Append the link to `li`
      li.append(a);
      // Append the `li' to the `ol` container element
      ol.append(li);
    });
    // Append to mobile wrapper
    if ($autoCompleteMobile) {
      $autoCompleteMobile.append(label);
      ol.id = $inputMobile.getAttribute("aria-controls") ?? ClassNames.search.list;
      $autoCompleteMobile.append(ol);
      $inputMobile.setAttribute("aria-expanded", "true");
    }
    // Append a clone to the desktop wrapper
    if ($autoCompleteDesktop) {
      $autoCompleteDesktop.append(label.cloneNode(true));
      const olClone = ol.cloneNode(true);
      olClone.id = $inputDesktop.getAttribute("aria-controls") ?? ClassNames.search.list;
      $autoCompleteDesktop.append(olClone);
      $inputDesktop.setAttribute("aria-expanded", "true");
    }
    // Hightlight user value
    highlightUserValue();
  }
};

/**
 * Handle all functionality for the clear button
 */
const handleClearButton = () => {
  function toggleClearBtn($input, $btn) {
    if (!$input || !$btn) return;
    if ($input.value === "") {
      $btn.classList.add(
        `${ClassNames.search.buttonClear}--${ClassNames.modifiers.hidden}`
      );
      $btn.tabIndex = -1;
    } else {
      $btn.classList.remove(
        `${ClassNames.search.buttonClear}--${ClassNames.modifiers.hidden}`
      );
      $btn.tabIndex = 0;
    }
  }

  function onClick(e, $input, $btn) {
    e.preventDefault();
    if (!$input || !$btn) return;
    $input.value = "";
    $input.focus();
    clearAutoCompleteResults();
    toggleClearBtn($input, $btn);
    evaluateSubmitDisabled();
  }

  $btnClearDesktop?.addEventListener("click", (e) => {
    onClick(e, $inputDesktop, $btnClearDesktop);
    onClick(e, $inputMobile, $btnClearMobile);
  });

  $inputDesktop?.addEventListener("keyup", () => {
    toggleClearBtn($inputDesktop, $btnClearDesktop);
    toggleClearBtn($inputMobile, $btnClearMobile);
  });

  $btnClearMobile?.addEventListener("click", (e) => {
    onClick(e, $inputMobile, $btnClearMobile);
    onClick(e, $inputDesktop, $btnClearDesktop);
  });

  $inputMobile?.addEventListener("keyup", () => {
    toggleClearBtn($inputMobile, $btnClearMobile);
    toggleClearBtn($inputDesktop, $btnClearDesktop);
  });
};

/**
 * Handle UI changes for mobile and desktop input focus
 */
const handleInputFocus = () => {
  // Mobile

  // Show auto complete container on focus in
  $formMobile?.addEventListener("focusin", () => {
    $autoCompleteMobile.classList.remove(
      `${ClassNames.search.autoComplete}--${ClassNames.modifiers.hidden}`
    );

    // Add focus state
    $inputMobile.parentElement.classList.add("is-in-focus");
    $formMobile.classList.add("a11y-focus");

    // Add accessiblity field when there are search results
    if (autoCompleteResponse?.length) {
      $inputMobile.setAttribute("aria-expanded", "true");
    }

    reevaluateOverlay();
  });

  // Hide auto complete container on focus out
  $formMobile?.addEventListener("focusout", () => {
    $autoCompleteMobile.classList.add(
      `${ClassNames.search.autoComplete}--${ClassNames.modifiers.hidden}`
    );

    // Reset state to non-focus
    $inputMobile.parentElement.classList.remove("is-in-focus");
    $formMobile.classList.remove("a11y-focus");

    // Reset accessiblity field
    $inputMobile.setAttribute("aria-expanded", "false");
  });

  // Desktop

  $formDesktop?.addEventListener(
    "focusin",
    () => {
      // Set width
      if ($inputDesktop.clientWidth < 300) {
        $inputDesktop.parentElement.style.width = "300px";
      }

      // Add focus state
      $inputDesktop.parentElement.classList.add("is-in-focus");
      $formDesktop.classList.add("a11y-focus");

      // show icon for clearing value input
      if ($inputDesktop?.value !== "") {
        $btnClearDesktop.classList.remove("hidden");
      }

      // Reenable submit button if input (and if disabled)
      if ($inputDesktop?.value !== "") {
        $submitDesktop.disabled = false;
      }

      // Show auto complete container
      $autoCompleteDesktop.classList.remove(
        `${ClassNames.search.autoComplete}--${ClassNames.modifiers.hidden}`
      );

      // Add accessiblity field when there are search results
      if (autoCompleteResponse?.length) {
        $inputDesktop.setAttribute("aria-expanded", "true");
      }

      // Clean up related interface
      isAnySubmenuOpen() && closeAllSubmenus();
      reevaluateOverlay();
    },
    true
  );

  $formDesktop?.addEventListener(
    "blur",
    () => {
      // Reset width to CSS default
      $inputDesktop.parentElement.style.width = "";

      // Reset state to non-focus
      $inputDesktop.parentElement.classList.remove("is-in-focus");
      $formDesktop.classList.remove("a11y-focus");

      // Reset auto complete container to CSS default
      $autoCompleteDesktop.classList.add(
        `${ClassNames.search.autoComplete}--${ClassNames.modifiers.hidden}`
      );

      // Reset accessiblity field
      $inputDesktop.setAttribute("aria-expanded", "false");

      // Disable submit button if field too narrow
      setTimeout(() => setSubmitDisabledIfInputIsToSmallDesktop(), 150);
    },
    true
  );
};

/**
 * Setup event listeners to validate input
 */
const handleInputValidation = () => {
  // For mobile
  $inputMobile?.addEventListener("keyup", () => evaluateSubmitDisabled());

  // For desktop
  $inputDesktop?.addEventListener("keyup", () => evaluateSubmitDisabled());
};

/**
 * Submit the forms
 */
const handleSubmitForms = () => {
  $formMobile?.addEventListener("submit", (e) => {
    e.preventDefault();
    window.location.href = $formMobile.action;
  });

  $formDesktop?.addEventListener("submit", (e) => {
    e.preventDefault();
    window.location.href = $formDesktop.action;
  });
};

/**
 * Highlights user value in suggested search values from API
 */
const highlightUserValue = () => {
  function highlight($links) {
    $links.forEach(($link) => {
      // Since both input values are kept in sync we can just take one of them
      const userValue = $inputMobile.value.toLowerCase();
      $link.innerHTML = $link.innerHTML
        .replace(/<\/?[^>]+(>|$)/g, "")
        .replace(new RegExp(userValue, "ig"), `<mark>$&</mark>`);
    });
  }

  const $mobileLinks = $autoCompleteMobile?.querySelectorAll("a");
  const $desktopLinks = $autoCompleteDesktop?.querySelectorAll("a");

  if ($mobileLinks?.length) highlight($mobileLinks);
  if ($desktopLinks?.length) highlight($desktopLinks);
};

/**
 * Request API for auto complete suggestions
 */
const requestAutoComplete = async (query) => {
  return fetch(`${ $inputMobile?.dataset?.api || $inputDesktop?.dataset?.api}?site=${site}&query=${query}`)
    .then((response) => response.json())
    .catch(() => []);
};

/**
 * Disable submit button if search field is narrower than 72px
 * This is to avoid accidental clicks on button
 * Button is re-enabled on focus in handleInputFocus
 */
const setSubmitDisabledIfInputIsToSmallDesktop = () => {
  if ($inputDesktop?.clientWidth < 72 && $submitDesktop) $submitDesktop.disabled = true;
};

/**
 * Initialize an eventlistener for showing autosuggestions below the searchinput box.
 * @param inputElement Searchbox with user input to find suggestions
 */
const initSearchSuggestionListener = (inputElement) => {
  function ignoreSearchingForKeys(event) {
    return ["ArrowDown", "ArrowUp"].indexOf(event.code) === -1;
  }

  function meetsMinimumLength(inputElement) {
    return inputElement.value?.length >= 1;
  }

  inputElement.addEventListener(
    "keyup",
    debounce(async (event) => {
      if (meetsMinimumLength(inputElement) && ignoreSearchingForKeys(event)) {
        try {
          await handleAutoComplete();
          highlightUserValue();
        } catch (error) {
          // ignore
        }
      } else if (!meetsMinimumLength(inputElement)) {
        clearAutoCompleteResults();
      }
    }, 500)
  );
};

/**
 * Initialize an eventlistener
 * @param inputElement Searchbox with user input to find suggestions
 * @param autoCompleteList Suggestionlist based on the seach inputbox
 */
const initSearchNavigationSupportListener = (
  inputElement,
  autoCompleteList
) => {
  if (!inputElement || !autoCompleteList) return;

  let selectedNode = null;

  const determineNextValidIndex = (eventCode, nodes) => {
    let currentIndex = selectedSearchResultIndex;
    let nextIndex = -1;
    switch (eventCode) {
      case "ArrowDown":
        nextIndex = ++currentIndex > nodes.length - 1 ? 0 : currentIndex;
        break;
      case "ArrowUp":
        nextIndex = --currentIndex < 0 ? nodes.length - 1 : currentIndex;
        break;
      default:
        nextIndex = -1;
    }

    return nextIndex;
  };

  const handleKeyUpNavigationListener = (event) => {
    const nodes = Array.prototype.slice.call(
      autoCompleteList.querySelectorAll("a"),
      0
    );

    selectedNode?.classList.remove("highlight");

    const nextIndex = determineNextValidIndex(event.code, nodes);

    if (nextIndex === -1) {
      selectedNode = null;
      return;
    }

    if (nextIndex in nodes) {
      selectedSearchResultIndex = nextIndex;
      selectedNode = nodes[nextIndex];
      selectedNode.classList.add("highlight");
      selectedNode.scrollIntoView({ block: "nearest" });

      inputElement.setAttribute("aria-activedescendant", `option-${nextIndex}`);
      inputElement.value = selectedNode.innerText;
      setSearchFormAction(inputElement);
    }
  };

  //Add eventlistener
  inputElement.addEventListener("keyup", handleKeyUpNavigationListener);
};

/**
 * Set up event listeners for both inputs triggering API requests and
 * highlighting auto complete values when input meets length requirement.
 */
const setupAutoComplete = () => {
  if ($inputMobile) {
    initSearchSuggestionListener($inputMobile);
    initSearchNavigationSupportListener($inputMobile, $autoCompleteMobile);
  }

  if ($inputDesktop) {
    initSearchSuggestionListener($inputDesktop);
    initSearchNavigationSupportListener($inputDesktop, $autoCompleteDesktop);
  }
};

export default initSearch;
